/* ==========================================================================
   NÉORA THEME JS
   1. Scroll reveal for .neora-reveal elements
   2. <neora-marquee> — CSS-animation marquee with duplication, resize
      handling, pause control and reduced-motion support
   ========================================================================== */

(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------
     Scroll reveal
     ------------------------------------------------------------------ */
  const initReveal = () => {
    const targets = document.querySelectorAll('.neora-reveal:not(.is-visible)');
    if (!targets.length) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    targets.forEach((el) => observer.observe(el));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReveal);
  } else {
    initReveal();
  }

  // Re-run when sections are re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', initReveal);

  /* ------------------------------------------------------------------
     Marquee
     Markup contract:
       <neora-marquee data-speed="60">            (speed = px per second)
         <div class="neora-marquee__track">
           <div class="neora-marquee__seg"> ...items... </div>
         </div>
         <button class="neora-marquee__pause" ...> (optional)
       </neora-marquee>
     ------------------------------------------------------------------ */
  if (!customElements.get('neora-marquee')) {
    customElements.define(
      'neora-marquee',
      class NeoraMarquee extends HTMLElement {
        connectedCallback() {
          if (this.initialized) return;
          this.initialized = true;

          this.track = this.querySelector('.neora-marquee__track');
          this.seg = this.querySelector('.neora-marquee__seg');
          if (!this.track || !this.seg) return;

          this.baseItems = Array.from(this.seg.children).map((node) => node.cloneNode(true));
          if (!this.baseItems.length) return;

          const pauseBtn = this.querySelector('.neora-marquee__pause');
          if (pauseBtn) {
            pauseBtn.addEventListener('click', () => {
              const paused = this.toggleAttribute('data-paused');
              pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
            });
          }

          this.build();

          let resizeTimer;
          this.resizeHandler = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => this.build(), 150);
          };
          window.addEventListener('resize', this.resizeHandler);
          reducedMotion.addEventListener?.('change', () => this.build());
        }

        disconnectedCallback() {
          if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
        }

        build() {
          // Reset to the authored items.
          this.querySelectorAll('.neora-marquee__seg--clone').forEach((clone) => clone.remove());
          this.seg.replaceChildren(...this.baseItems.map((node) => node.cloneNode(true)));

          if (reducedMotion.matches) return;

          // Duplicate items until the segment fills the viewport width,
          // so the -50% translate loops without a visible gap.
          const containerWidth = this.clientWidth || window.innerWidth;
          let copies = 0;
          while (this.seg.scrollWidth < containerWidth && copies < 12) {
            this.baseItems.forEach((node) => this.seg.appendChild(node.cloneNode(true)));
            copies += 1;
          }

          // Second, screen-reader-hidden segment completes the loop.
          const clone = this.seg.cloneNode(true);
          clone.classList.add('neora-marquee__seg--clone');
          clone.setAttribute('aria-hidden', 'true');
          this.track.appendChild(clone);

          const speed = Math.max(parseFloat(this.dataset.speed) || 60, 10);
          const duration = this.seg.scrollWidth / speed;
          this.style.setProperty('--neora-marquee-duration', `${duration.toFixed(2)}s`);
        }
      }
    );
  }
})();
